import "./Table.scss";

import React, { FunctionComponent, useState } from "react";
import { DataGrid, type GridPaginationModel } from "@mui/x-data-grid";
import { SvgIcon, Box, IconButton } from "@mui/material";
import { ICustomFooter, ICustomToolbar, TableProps } from "./ITable";
import CustomToolbar from "./components/CustomToolbar";
import CustomFooter from "./components/CustomFooter";
import { ReactComponent as Search } from "../../Assets/Images/search.svg";
import { ReactComponent as NoData } from "../../Assets/Images/NoData.svg";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const Table: FunctionComponent<TableProps> = (props) => {
  const {
    rows,
    columns,
    density,
    loading,
    isCellEditable,
    columnVisibilityModel,
    disableColumnMenu,
    checkboxSelection,
    getRowId,
    rowCount,
    rowHeight,
    children,
    initialPageSize,
    currentPage = 1,
    totalPage = 1,
    pageSize: propPageSize = initialPageSize ?? 20,
    onPageChange,
    pageSizeOptions,
    localeText,
    processRowUpdate,
    editMode,
    rowModesModel,
    onRowModesModelChange,
    onRowEditStop,
    getRowClassName,
    paginationMode = "client",
    initialSearchValue,
    sx = {
      boxShadow: "0px 0px 2px 0px rgba(145, 158, 171, 0.2)",
      borderRadius: "16px",
      "& .MuiDataGrid-row.Mui-hovered": { backgroundColor: "transparent" },
      "& .MuiDataGrid-row:hover": { backgroundColor: "transparent" },
    },
  } = props;

  // Classify children
  const customToolbar = React.Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.type === CustomToolbar
  );

  const customFooter = React.Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.type === CustomFooter
  );

  const customToolbarProps: ICustomToolbar | {} =
    React.isValidElement(customToolbar) && customToolbar.props
      ? (customToolbar.props as ICustomToolbar)
      : {};

  const customFooterProps: ICustomFooter | {} =
    React.isValidElement(customFooter) && customFooter.props
      ? (customFooter.props as ICustomFooter)
      : {};

  // Client-side pagination state
  const [clientPaginationModel, setClientPaginationModel] = useState<GridPaginationModel>({
    pageSize: initialPageSize ?? 20,
    page: 0,
  });

  const isServerMode = paginationMode === "server";

  const serverPaginationModel: GridPaginationModel = {
    page: currentPage - 1,
    pageSize: propPageSize,
  };

  const paginationModelToUse: GridPaginationModel = isServerMode ? serverPaginationModel : clientPaginationModel;

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    if (isServerMode) {
      onPageChange?.(model.page + 1);
    } else {
      setClientPaginationModel(model);
    }
  };

  const totalPagesToUse = isServerMode ? totalPage : Math.ceil((rows.length || 0) / paginationModelToUse.pageSize);
  const rowCountToUse = isServerMode ? (rowCount ?? 0) : undefined;
  const pageSizeOptionsToUse = isServerMode ? [propPageSize] : (pageSizeOptions ?? [20, 50, 100]);

  // Custom footer with pagination arrows
  const FooterWithArrows: FunctionComponent = () => (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      px={2}
      py={1}
      width="100%"
      sx={{ backgroundColor: "#fff", borderTop: "1px solid #e0e0e0" }}
    >
      {/* Left side: arrows */}
      <Box display="flex" alignItems="center" gap={1}>
        <IconButton
          onClick={() => {
            handlePaginationModelChange({
              ...paginationModelToUse,
              page: Math.max(0, paginationModelToUse.page - 1),
            });
          }}
          disabled={paginationModelToUse.page === 0}
          size="small"
        >
          <ArrowBackIosNewIcon fontSize="small" />
        </IconButton>
        <IconButton
          onClick={() => {
            handlePaginationModelChange({
              ...paginationModelToUse,
              page: Math.min(paginationModelToUse.page + 1, totalPagesToUse - 1),
            });
          }}
          disabled={paginationModelToUse.page + 1 >= totalPagesToUse}
          size="small"
        >
          <ArrowForwardIosIcon fontSize="small" />
        </IconButton>
        <span style={{ fontSize: "14px", fontWeight: "600" }}>
          Page {paginationModelToUse.page + 1} of {totalPagesToUse}
        </span>
      </Box>

      {/* Right side: optional custom footer or info */}
      {customFooter && <CustomFooter {...customFooterProps} />}
    </Box>
  );

  return (
    <div style={{ height: "calc(100vh - 80px)", width: "100%" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        editMode={editMode}
        rowModesModel={rowModesModel}
        onRowModesModelChange={onRowModesModelChange}
        onRowEditStop={onRowEditStop}
        getRowClassName={getRowClassName}
        localeText={{
          ...localeText,
          toolbarExport: "Export",
          toolbarExportCSV: "CSV",
          toolbarQuickFilterPlaceholder: "Search",
        }}
        sx={sx}
        isCellEditable={isCellEditable}
        className="Table"
        autoHeight={false}
        density={density ?? "standard"}
        loading={loading}
        disableColumnMenu={disableColumnMenu ?? true}
        checkboxSelection={checkboxSelection}
        getRowId={getRowId}
        rowHeight={rowHeight}
        getRowHeight={() => "auto"}
        pagination
        paginationMode={paginationMode}
        paginationModel={paginationModelToUse}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={pageSizeOptionsToUse}
        rowCount={rowCountToUse}
        processRowUpdate={processRowUpdate}
        slots={{
          toolbar: customToolbar ? CustomToolbar : undefined,
          footer: FooterWithArrows,
          noRowsOverlay: CustomNoRows,
          quickFilterIcon: Search,
        }}
        slotProps={{
          toolbar: {
            ...customToolbarProps,
            showQuickFilter: true,
            exportButton: true,
            exportCsv: { text: "CSV" },
          },
        }}
        initialState={{
          columns: { columnVisibilityModel },
          filter: {
            filterModel: {
              items: [],
              quickFilterValues: initialSearchValue ? [initialSearchValue] : [],
            },
          },
        }}
      />
    </div>
  );
};

export default Table;

// ---------------- Custom No Rows Overlay ----------------
interface CustomNoRowsProps { }
const CustomNoRows: FunctionComponent<CustomNoRowsProps> = () => {
  return (
    <div className="custom_no_rows">
      <SvgIcon className="table_svg_icon" component={NoData} inheritViewBox />
      No Data Found
    </div>
  );
};